/**
 * ServicesNumbered — bold numbered grid of service cards with dark hover fill.
 * Ideal for brands that want a strong, structured portfolio layout.
 *
 * Registered as: services / services-numbered
 */
import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { SectionComponentProps } from "../../registry.js";

gsap.registerPlugin(ScrollTrigger);

interface ServiceItem {
  name: string;
  description?: string;
  eyebrow?: string;
  priceLabel?: string;
}

interface ServicesNumberedProps {
  headline?: string;
  subheadline?: string;
  services?: ServiceItem[];
  ctaLabel?: string;
  ctaTarget?: string;
}

const DEFAULT_SERVICES: ServiceItem[] = [
  {
    name: "Precision Mowing",
    description: "Weekly cuts with edging, blowout, and same-crew consistency all season.",
    eyebrow: "Maintenance",
    priceLabel: "From $35/visit",
  },
  {
    name: "Spring Cleanup",
    description: "Power raking, bed edging, and first fertilization to kickstart the season.",
    eyebrow: "Seasonal",
    priceLabel: "From $180",
  },
  {
    name: "Fall Maintenance",
    description: "Core aeration, final cut, and soil fortification before winter sets in.",
    eyebrow: "Seasonal",
    priceLabel: "From $150",
  },
  {
    name: "Snow Removal",
    description: "Rapid deployment — path cleared before the day begins.",
    eyebrow: "Tactical",
    priceLabel: "Custom pricing",
  },
];

function scrollToHash(target: string) {
  const id = target.startsWith("#") ? target.slice(1) : target;
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
}

export function ServicesNumbered({ props }: SectionComponentProps) {
  const {
    headline = "Service Portfolio",
    subheadline,
    services = DEFAULT_SERVICES,
    ctaLabel = "Book Now",
    ctaTarget = "#quote",
  } = props as ServicesNumberedProps;

  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      // ── 1. Headline: per-word clip reveal ────────────────────────────────
      const h2 = el.querySelector<HTMLElement>("h2");
      if (h2) {
        const text = h2.textContent ?? "";
        h2.innerHTML = text
          .trim()
          .split(/\s+/)
          .map(
            (w) =>
              `<span style="display:inline-block;overflow:hidden;vertical-align:bottom;line-height:1.05">` +
              `<span class="sn-hword" style="display:inline-block">${w}</span></span>`,
          )
          .join(" ");

        gsap.fromTo(
          ".sn-hword",
          { yPercent: 115, opacity: 0 },
          {
            yPercent: 0,
            opacity: 1,
            duration: 0.9,
            ease: "power3.out",
            stagger: 0.07,
            scrollTrigger: { trigger: el, start: "top 82%" },
          },
        );
      }

      // ── 2. Subheadline: slide in from left ───────────────────────────────
      const sub = el.querySelector<HTMLElement>(".sn-sub");
      if (sub) {
        gsap.fromTo(
          sub,
          { opacity: 0, x: -32 },
          {
            opacity: 1,
            x: 0,
            duration: 0.7,
            ease: "power3.out",
            delay: 0.35,
            scrollTrigger: { trigger: el, start: "top 80%" },
          },
        );
      }

      // ── 3. Cards: stagger cascade — y + scale + rotateX ─────────────────
      const grid = el.querySelector<HTMLElement>(".sn-grid");
      const cards = el.querySelectorAll<HTMLElement>(".sn-card");
      if (cards.length && grid) {
        gsap.set(grid, { perspective: 800 });
        gsap.fromTo(
          cards,
          { opacity: 0, y: 72, scale: 0.93, rotateX: 12 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            rotateX: 0,
            duration: 0.85,
            ease: "power3.out",
            stagger: 0.1,
            transformOrigin: "top center",
            scrollTrigger: { trigger: grid, start: "top 88%" },
          },
        );
      }

      // ── 4. Numbers inside cards: clip reveal after cards land ─────────────
      const nums = el.querySelectorAll<HTMLElement>(".sn-num");
      if (nums.length) {
        gsap.fromTo(
          nums,
          { clipPath: "inset(100% 0 0 0)" },
          {
            clipPath: "inset(0% 0 0 0)",
            duration: 0.55,
            ease: "power4.out",
            stagger: 0.1,
            scrollTrigger: { trigger: el.querySelector(".sn-grid"), start: "top 88%" },
            delay: 0.2,
          },
        );
      }
    }, el);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 px-6 lg:px-16 bg-background" id="services">
      <div className="mb-16">
        <h2 className="font-display text-5xl md:text-7xl font-bold uppercase text-foreground leading-none mb-6">
          {headline}
        </h2>
        {subheadline && (
          <p className="sn-sub text-muted-foreground text-lg leading-relaxed border-l-4 border-primary pl-6 max-w-lg">
            {subheadline}
          </p>
        )}
      </div>

      <div className="sn-grid grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
        {services.map((svc, i) => (
          <div
            key={svc.name ?? i}
            className="sn-card group bg-background p-10 transition-colors duration-300 hover:bg-foreground"
          >
            <div className="sn-num font-display text-5xl font-bold text-primary mb-6 group-hover:text-background transition-colors duration-300">
              {String(i + 1).padStart(2, "0")}
            </div>
            {svc.eyebrow && (
              <div className="text-primary group-hover:text-background text-[10px] font-bold uppercase tracking-[0.3em] mb-3 transition-colors duration-300">
                {svc.eyebrow}
              </div>
            )}
            <h3 className="font-display text-xl font-bold uppercase mb-4 text-foreground group-hover:text-background transition-colors duration-300 leading-tight">
              {svc.name}
            </h3>
            {svc.description && (
              <p className="text-sm text-muted-foreground leading-relaxed mb-6 group-hover:text-background/80 transition-colors duration-300">
                {svc.description}
              </p>
            )}
            {svc.priceLabel && (
              <p className="text-xs font-bold text-foreground/40 group-hover:text-background/60 transition-colors duration-300 mb-6">
                {svc.priceLabel}
              </p>
            )}
            <a
              href={ctaTarget}
              onClick={(e) => { if (ctaTarget?.startsWith("#")) { e.preventDefault(); scrollToHash(ctaTarget); } }}
              className="inline-flex items-center gap-1.5 font-bold text-xs uppercase tracking-widest text-foreground group-hover:text-background transition-colors duration-300"
            >
              {ctaLabel}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}
